import { ReactNode } from 'react';
import { cn } from '@v2/shared/lib/utils';

export type PageHeadingProps = {
  /** The H1's words. */
  children: ReactNode;
  /**
   * A word or two above the title naming the part of the site the page belongs to —
   * "Customer Information" on the institutional pages. Chrome rather than page copy.
   */
  eyebrow?: string;
  /** The page's opening line, on the pages that have one. */
  intro?: ReactNode;
  /**
   * The calls to action that belong to the heading itself, on a page whose design puts
   * them in the band — /affiliate-program and /discountlock.
   *
   * A sibling of the intro rather than something you pass inside it, and that is the
   * whole point: `intro` renders in a paragraph carrying `opacity-90`, and opacity
   * composites the entire subtree it is set on. A white button inside that paragraph
   * paints at 90% over the gradient and comes out pink, and no `opacity-100` on the
   * button can undo it — a child cannot opt out of an ancestor's opacity.
   *
   * Whatever you put here has to be legible on the band, which is a `primary` gradient.
   * A bare `ButtonLink` is `bg-primary text-white` and so lands red-on-red — barely
   * visible, and on a brand whose two reds are close, not visible at all. Give it the
   * on-gradient treatment: `bg-white text-primary-700` for the filled one, a
   * `border-white/70 bg-transparent text-white` outline for the secondary.
   */
  actions?: ReactNode;
  /**
   * The small print that closes the band, under the actions — the terms line on
   * /discountlock. Smaller than the intro and last in the band.
   */
  footnote?: ReactNode;
  /**
   * The taller rhythm, for a band with a strip of figures pulled up over its bottom
   * edge: /guarantee, /reviews, /about-us and /discountlock.
   *
   * It is the floor those four share, not the only floor there is. The strip decides how
   * much room it needs, because the strip decides how far it is pulled up — /refund pulls
   * its own 24px and /shipping 40px, and both set their floor with `className` instead.
   * The pairing is the thing to keep: a floor here and a pull there that do not add up
   * leave the last line of copy sitting on the strip, or a stripe of empty red under it.
   */
  hero?: boolean;
  className?: string;
};

/**
 * A page's H1, in the coloured band the design puts it in.
 *
 * Every red band on the site is this component. /guarantee, /reviews, /about-us and
 * /discountlock each drew their own for a while, and the four of them plus this one had
 * drifted into three heading sizes, two eyebrow weights, two corner radii, two gradients
 * and a decorative disc that three of them had and two did not. A page that wants the
 * band asks for it here; a page that wants something the band cannot do is a change to
 * this file, where every page gets it at once.
 *
 * The band is one size on every page. It used to grow a step from `md` up when a page
 * passed an opening line, on the reasoning that the taller band needed the extra copy to
 * fill it; in practice only one page has an intro, so the rule read as that page's
 * heading being wrong rather than as a size the design has. Carrying an intro says
 * nothing about how big the heading should be, so it no longer decides.
 *
 * `primary` is themed per brand, so the gradient is whichever red the site is wearing.
 */
export default function PageHeading({
  children,
  eyebrow,
  intro,
  actions,
  footnote,
  hero,
  className,
}: PageHeadingProps) {
  return (
    <div
      className={cn(
        // Not `overflow-hidden`, and that is a decision. It arrived on this line paired
        // with the decorative disc — an `after:` pseudo-element hung off `-right-16
        // -top-16` — and existed to clip it back to the rounded rectangle. 095d533b4eb
        // deleted the disc from every band and left the clip behind, where the only
        // thing left for it to cut is whatever a page puts in `actions`: it took the
        // help centre's search suggestions off at the band's bottom edge. The gradient
        // is clipped by `rounded-xl` on its own; nothing here needs an overflow rule.
        'relative rounded-xl px-6 text-white md:px-10',
        hero ? 'pb-24 pt-9' : 'py-7 md:py-6',
        'bg-gradient-to-br from-primary-500 via-primary to-primary-700',
        className
      )}
    >
      {eyebrow ? (
        <p className="m-0 mb-2 text-xs font-bold uppercase tracking-widest opacity-90">{eyebrow}</p>
      ) : null}

      <h1 className="m-0 text-3xl font-extrabold leading-tight tracking-tight">{children}</h1>

      {intro ? <p className="mb-0 mt-2 text-base leading-snug opacity-90">{intro}</p> : null}

      {actions ? <div className="mt-4 flex flex-wrap gap-3">{actions}</div> : null}

      {footnote ? <p className="mb-0 mt-4 text-xs opacity-90">{footnote}</p> : null}
    </div>
  );
}
